/**
 * Role Selector Page
 * Shows when user has multiple roles, allowing them to choose which dashboard to enter
 * Supports "Remember My Choice" to skip this screen on future logins
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Link2, Shield, Truck, Store, Package, Users, BarChart3, Settings } from "lucide-react";
import api from "../lib/api";

// Role display configuration
const ROLE_CONFIG = {
  ADMIN: {
    title: "Administrator",
    description: "Full system access and management",
    icon: Shield,
    path: "/admin",
    color: "from-red-500 to-red-600"
  },
  CEO: {
    title: "CEO Dashboard",
    description: "Executive overview and analytics",
    icon: BarChart3,
    path: "/admin",
    color: "from-purple-500 to-purple-600"
  },
  STAFF: {
    title: "Staff Portal",
    description: "Customer support and operations",
    icon: Users,
    path: "/admin",
    color: "from-blue-500 to-blue-600"
  },
  DRIVER: {
    title: "Driver Portal",
    description: "Deliveries and earnings management",
    icon: Truck,
    path: "/driver",
    color: "from-green-500 to-green-600"
  },
  BUSINESS_MERCHANT: {
    title: "Business Dashboard",
    description: "Orders, products and analytics",
    icon: Store,
    path: "/merchant",
    color: "from-amber-500 to-amber-600"
  },
  PUDO_POINT: {
    title: "PUDO Point",
    description: "Package handling and storage",
    icon: Package,
    path: "/pudo",
    color: "from-cyan-500 to-cyan-600"
  },
  PUDO_OPERATOR: {
    title: "PUDO Operator",
    description: "Package handling and storage",
    icon: Package,
    path: "/pudo",
    color: "from-cyan-500 to-cyan-600"
  },
  FULFILLMENT: {
    title: "Fulfillment Center",
    description: "Order processing and dispatch",
    icon: Package,
    path: "/fulfillment",
    color: "from-indigo-500 to-indigo-600"
  },
  CUSTOMER: {
    title: "Customer Account",
    description: "Orders and tracking",
    icon: Users,
    path: "/dashboard",
    color: "from-slate-500 to-slate-600"
  }
};

// LocalStorage key for remembering role preference
const REMEMBERED_ROLE_KEY = "liantiao_remembered_role";

export const getRememberedRole = () => {
  try {
    const stored = localStorage.getItem(REMEMBERED_ROLE_KEY);
    if (stored) {
      const { role, userId } = JSON.parse(stored);
      return { role, userId };
    }
  } catch (e) {
    console.error("Error reading remembered role:", e);
  }
  return null;
};

export const setRememberedRole = (role, userId) => {
  try {
    localStorage.setItem(REMEMBERED_ROLE_KEY, JSON.stringify({ role, userId }));
  } catch (e) {
    console.error("Error saving remembered role:", e);
  }
};

export const clearRememberedRole = () => {
  try {
    localStorage.removeItem(REMEMBERED_ROLE_KEY);
  } catch (e) {
    console.error("Error clearing remembered role:", e);
  }
};

const RoleSelector = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [rememberChoice, setRememberChoice] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const roles = user?.roles || [];
  
  // Filter out CUSTOMER role if there are other roles
  const displayRoles = roles.length > 1 
    ? roles.filter(r => r !== "CUSTOMER")
    : roles;

  // Check for remembered role on mount
  useEffect(() => {
    const remembered = getRememberedRole();
    if (remembered && remembered.userId === user?.id && roles.includes(remembered.role)) {
      // Auto-select the remembered role
      handleSelectRole(remembered.role, true);
    }
  }, [user?.id]);

  const handleSelectRole = async (role, isAutoSelect = false) => {
    setIsLoading(true);
    setSelectedRole(role);

    try {
      // Call switch-role API to get new token with activeRole
      const response = await api.post("/auth/switch-role", { role });
      
      // Update local storage with new token
      localStorage.setItem("token", response.data.access_token);
      
      // Save role preference if checkbox is checked (or if auto-selecting a remembered role)
      if (rememberChoice && !isAutoSelect) {
        setRememberedRole(role, user.id);
        toast.success(`Switched to ${ROLE_CONFIG[role]?.title || role}. Your choice has been saved.`);
      } else if (!isAutoSelect) {
        toast.success(`Switched to ${ROLE_CONFIG[role]?.title || role}`);
      }
      
      // Update user context
      if (updateUser) {
        updateUser({
          ...user,
          activeRole: role
        });
      }
      
      // Navigate to the role-specific dashboard
      const config = ROLE_CONFIG[role];
      const pendingRedirect = sessionStorage.getItem("postLoginRedirect"); if (pendingRedirect) { sessionStorage.removeItem("postLoginRedirect"); navigate(pendingRedirect, { replace: true }); } else { navigate(config?.path || "/dashboard", { replace: true }); }
    } catch (error) {
      console.error("Error switching role:", error);
      toast.error("Failed to switch role. Please try again.");
      setIsLoading(false);
      setSelectedRole(null);
    }
  };

  const handleClearPreference = () => {
    clearRememberedRole();
    toast.success("Role preference cleared. You will see this screen on next login.");
    setShowSettings(false);
  };

  if (!user) {
    navigate("/login", { replace: true });
    return null;
  }

  // Check if user has a saved preference
  const remembered = getRememberedRole();
  const hasRememberedRole = remembered && remembered.userId === user.id;

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center px-4 py-8">
      {/* Logo */}
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Link2 className="h-8 w-8 text-amber-500" />
          <span className="text-2xl font-bold text-white tracking-tight font-['Manrope']">
            Liantiao
          </span>
        </div>
        <p className="text-slate-400 text-sm font-['Manrope']">Select your dashboard</p>
      </div>

      {/* Welcome message */}
      <div className="mb-8 text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
          Welcome, {user.name}!
        </h1>
        <p className="text-slate-400">
          You have access to multiple dashboards. Choose where to go:
        </p>
      </div>

      {/* Role Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl w-full mb-6">
        {displayRoles.map((role) => {
          const config = ROLE_CONFIG[role] || {
            title: role,
            description: "Access your dashboard",
            icon: Users,
            path: "/dashboard",
            color: "from-slate-500 to-slate-600"
          };
          const Icon = config.icon;
          const isSelected = selectedRole === role && isLoading;

          return (
            <button
              key={role}
              data-testid={`role-card-${role.toLowerCase()}`}
              onClick={() => handleSelectRole(role)}
              disabled={isLoading}
              className={`
                relative overflow-hidden rounded-lg p-6 text-left transition-all duration-300
                bg-slate-900 border border-slate-800 hover:border-slate-700
                hover:shadow-lg hover:shadow-amber-500/10 hover:-translate-y-1
                disabled:opacity-50 disabled:cursor-not-allowed
                ${isSelected ? "ring-2 ring-amber-500" : ""}
              `}
            >
              {/* Gradient accent */}
              <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${config.color}`} />
              
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg bg-gradient-to-br ${config.color} text-white`}>
                  {isSelected ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    <Icon className="h-6 w-6" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">
                    {config.title}
                  </h3>
                  <p className="text-sm text-slate-400">
                    {config.description}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Remember choice checkbox */}
      <div className="flex items-center gap-3 mb-6">
        <Checkbox
          id="remember-role"
          checked={rememberChoice}
          onCheckedChange={setRememberChoice}
          className="border-slate-600 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
          data-testid="remember-role-checkbox"
        />
        <label 
          htmlFor="remember-role" 
          className="text-sm text-slate-400 cursor-pointer hover:text-slate-300"
        >
          Remember my choice (skip this screen next time)
        </label>
      </div>

      {/* Settings/Clear preference */}
      {hasRememberedRole && (
        <div className="mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
            className="text-slate-500 hover:text-slate-300 text-xs"
            data-testid="role-settings-btn"
          >
            <Settings className="h-3 w-3 mr-1" />
            Manage saved preference
          </Button>
          {showSettings && (
            <div className="mt-2 p-3 bg-slate-900 rounded-lg border border-slate-800">
              <p className="text-xs text-slate-400 mb-2">
                Currently remembering: <span className="text-amber-500 font-medium">{ROLE_CONFIG[remembered.role]?.title || remembered.role}</span>
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearPreference}
                className="text-xs border-red-800 text-red-400 hover:bg-red-900/20"
                data-testid="clear-preference-btn"
              >
                Clear saved preference
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Logout option */}
      <div className="mt-4">
        <Button
          variant="ghost"
          onClick={() => {
            clearRememberedRole();
            localStorage.removeItem("token");
            navigate("/login", { replace: true });
          }}
          className="text-slate-400 hover:text-white"
          data-testid="logout-from-role-selector"
        >
          Sign out
        </Button>
      </div>
    </div>
  );
};

export default RoleSelector;
